
import React, { useEffect, useState } from 'react';
import { supabase, getErrorMessage, isMockMode, getCurrentOrganisationId } from '../services/supabaseClient';
import {
  Loader2, Save, User, Bell,
  AlertCircle, CheckCircle2, Palette, Image as ImageIcon,
  Users, Shield, Plus, Trash2, Key, ShieldCheck, AlertTriangle, Car,
  Receipt, FileText, CreditCard, ExternalLink, Link as LinkIcon
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../App';
import { UserRole, UserProfile, BusinessModel, LegalEntityVerificationStatus } from '../types';
import Modal from '../components/Modal';
import { useSubscription } from '../context/SubscriptionContext';
import DriverBillingSettings from '../components/DriverBillingSettings';
import AuditLogs from '../components/AuditLogs';
import { AuditService } from '../services/auditService';
import { isAdminRole, normalizeUserRole } from '../utils/roles';

// Mock Team Data
const MOCK_TEAM: UserProfile[] = [
  { id: 'u1', name: 'Alice Owner', email: 'alice@fleet.com', role: 'owner', avatar_url: '' },
  { id: 'u2', name: 'Bob Admin', email: 'bob@fleet.com', role: 'admin', avatar_url: '' },
  { id: 'u3', name: 'Charlie Viewer', email: 'charlie@fleet.com', role: 'viewer', avatar_url: '' },
];

interface BusinessModelCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  color: 'primary' | 'amber' | 'blue';
}

type SettingsTabId = 'profile' | 'team' | 'organization' | 'driver-billing' | 'audit-logs';

interface SettingsTab {
  id: SettingsTabId;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

const SETTINGS_TABS: SettingsTab[] = [
  { id: 'profile', label: 'My Profile', icon: User },
  { id: 'team', label: 'Team Management', icon: Users, adminOnly: true },
  { id: 'organization', label: 'Organization', icon: Palette, adminOnly: true },
  { id: 'driver-billing', label: 'Driver Billing', icon: Receipt, adminOnly: true },
  { id: 'audit-logs', label: 'Audit Logs', icon: FileText, adminOnly: true },
];

const getRequestedTab = (): SettingsTabId | null => {
  const hashQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?') + 1)
    : '';
  const params = new URLSearchParams(hashQuery || window.location.search);
  const tab = params.get('tab');

  if (SETTINGS_TABS.some((item) => item.id === tab)) {
    return tab as SettingsTabId;
  }

  if (params.get('stripe_connect') || params.get('stripe_refresh')) {
    return 'driver-billing';
  }

  return null;
};

const BusinessModelCard: React.FC<BusinessModelCardProps> = ({
  icon: Icon,
  title,
  description,
  selected,
  onClick,
  color
}) => {
  const colorClasses = {
    primary: {
      border: 'border-primary-500 bg-primary-50 dark:bg-primary-900/20',
      iconBg: 'bg-primary-100 dark:bg-primary-800',
      iconColor: 'text-primary-600 dark:text-primary-400'
    },
    amber: {
      border: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20',
      iconBg: 'bg-amber-100 dark:bg-amber-800',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    blue: {
      border: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
      iconBg: 'bg-blue-100 dark:bg-blue-800',
      iconColor: 'text-blue-600 dark:text-blue-400'
    }
  };

  const colors = colorClasses[color];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left border-2 rounded-xl p-4 transition-all ${
        selected
          ? colors.border
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg ${
            selected ? colors.iconBg : 'bg-slate-100 dark:bg-slate-800'
          }`}
        >
          <Icon
            size={20}
            className={selected ? colors.iconColor : 'text-slate-400'}
          />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">
            {title}
          </h4>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
};

const Settings: React.FC = () => {
  const { branding, updateBranding, organization } = useBranding();
  const { role, setMockRole, session } = useAuth();
  const { tier } = useSubscription();

  const [activeTab, setActiveTab] = useState<SettingsTabId>(() => getRequestedTab() ?? 'profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [initialBusinessModel, setInitialBusinessModel] = useState<BusinessModel | null>(null);

  // Profile Form State
  const [email, setEmail] = useState('');
  const [notifications, setNotifications] = useState(true);

  // Organization Form State
  const [companyName, setCompanyName] = useState('');
  const [brandingColor, setBrandingColor] = useState('#2563eb');
  const [logoUrl, setLogoUrl] = useState('');

  // Operating Profile State
  const [businessModel, setBusinessModel] = useState<BusinessModel>('hire_firm_registered');
  const [enableHireWorkflows, setEnableHireWorkflows] = useState(true);
  const [enableTransferFeatures, setEnableTransferFeatures] = useState(true);
  const [, setLegalEntityVerificationStatus] =
    useState<LegalEntityVerificationStatus>('unverified');

  // Stripe Connect State
  const [stripeConnectAccountId, setStripeConnectAccountId] = useState<string | null>(null);
  const [stripeConnectStatus, setStripeConnectStatus] = useState<'not_connected' | 'pending' | 'active' | 'restricted' | 'disabled'>('not_connected');
  const [stripeChargesEnabled, setStripeChargesEnabled] = useState(false);
  const [stripePayoutsEnabled, setStripePayoutsEnabled] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  
  const selectBusinessModel = (model: BusinessModel) => {
    const wasFleetMode = businessModel === 'fleet_operator';
    setBusinessModel(model);

    if (model === 'fleet_operator' && !wasFleetMode) {
      setEnableHireWorkflows(false);
      setEnableTransferFeatures(false);
    }
    if (model !== 'fleet_operator') {
      setEnableHireWorkflows(true);
      setEnableTransferFeatures(true);
    }

    // Company verification is not self-certified here. Evidence readiness is checked per PCN.
    setLegalEntityVerificationStatus(model === 'fleet_operator' ? 'not_applicable' : 'unverified');
  };

  // Team Management State
  const [teamMembers, setTeamMembers] = useState<UserProfile[]>([]);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'viewer' as UserRole });
  const [teamLoading, setTeamLoading] = useState(false);
  const isTrialWorkspace = tier === 'trial' || organization?.trial_status === 'active';
  const canManageTeam = isAdminRole(role);
  const visibleTabs = SETTINGS_TABS.filter((tab) => !tab.adminOnly || canManageTeam);

  useEffect(() => {
    void getProfile();
  }, [role]);

  useEffect(() => {
    const requestedTab = getRequestedTab();
    if (!requestedTab) return;

    const requestedTabConfig = SETTINGS_TABS.find((tab) => tab.id === requestedTab);
    if (!requestedTabConfig?.adminOnly || canManageTeam) {
      setActiveTab(requestedTab);
    }
  }, [canManageTeam]);

  useEffect(() => {
    const activeTabConfig = SETTINGS_TABS.find((tab) => tab.id === activeTab);
    if (activeTabConfig?.adminOnly && !canManageTeam) {
      setActiveTab('profile');
    }
  }, [activeTab, canManageTeam]);

  // Check for Stripe Connect callback
  useEffect(() => {
    const hashQuery = window.location.hash.includes('?')
      ? window.location.hash.slice(window.location.hash.indexOf('?') + 1)
      : '';
    const urlParams = new URLSearchParams(hashQuery || window.location.search);
    const stripeConnectSuccess = urlParams.get('stripe_connect');
    const stripeRefresh = urlParams.get('stripe_refresh');

    if (stripeConnectSuccess === 'success' || stripeRefresh === 'true') {
      if (canManageTeam) {
        setActiveTab('driver-billing');
      }

      // Refresh account status after Stripe onboarding
      if (organization?.stripe_connect_account_id) {
        void handleRefreshStripeAccount();
      }

      // Clean up URL parameters
      const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTeam, organization?.stripe_connect_account_id]);

  useEffect(() => {
    if (canManageTeam && activeTab === 'team') {
      void loadTeam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTeam, activeTab]);

  const loadTeam = async () => {
    if (!canManageTeam) return;
    setTeamLoading(true);
    try {
      if (isMockMode) {
        setTeamMembers(MOCK_TEAM);
        return;
      }

      const orgId = await getCurrentOrganisationId();
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, avatar_url, created_at')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const members: UserProfile[] = (data ?? []).map((p: any) => ({
        id: p.id,
        email: p.email ?? '',
        name: p.full_name ?? p.email ?? 'Team Member',
        role: normalizeUserRole(p.role),
        avatar_url: p.avatar_url ?? '',
      }));

      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team:', error);
      setMessage({ type: 'error', text: 'Failed to load team members.' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setTeamLoading(false);
    }
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      
      if (isMockMode) {
        setEmail('demo@pcn-guard.com');
        setCompanyName(branding.companyName);
        setBrandingColor(branding.primaryColor);
        setLogoUrl(branding.logoUrl || '');
        setLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setEmail(user.email || '');
        // Org-level branding now comes from `organisations` via BrandingContext
        setCompanyName(branding.companyName);
        setBrandingColor(branding.primaryColor);
        setLogoUrl(branding.logoUrl || '');

        // Load organization business model and verification status
        if (organization) {
          setBusinessModel(organization.business_model || 'hire_firm_registered');
          setInitialBusinessModel(organization.business_model || 'hire_firm_registered');
          setEnableHireWorkflows(
            organization.business_model === 'fleet_operator'
              ? organization.enable_hire_workflows === true
              : true
          );
          setEnableTransferFeatures(
            organization.business_model === 'fleet_operator'
              ? organization.enable_transfer_features === true
              : true
          );
          setLegalEntityVerificationStatus(
            organization.legal_entity_verification_status || 'unverified'
          );

          // Load Stripe Connect status
          setStripeConnectAccountId(organization.stripe_connect_account_id || null);
          setStripeConnectStatus(organization.stripe_connect_status || 'not_connected');
          setStripeChargesEnabled(organization.stripe_connect_charges_enabled || false);
          setStripePayoutsEnabled(organization.stripe_connect_payouts_enabled || false);
        }

        if (user.user_metadata?.email_notifications !== undefined) {
          setNotifications(user.user_metadata.email_notifications);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setMessage({ type: 'error', text: 'Failed to load user profile.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      if (canManageTeam && activeTab === 'organization') {
        const nextEnableHireWorkflows =
          businessModel === 'fleet_operator' ? enableHireWorkflows : true;
        const nextEnableTransferFeatures =
          businessModel === 'fleet_operator'
            ? (nextEnableHireWorkflows ? enableTransferFeatures : false)
            : true;

        if (!isMockMode) {
          // Save business model and organization settings
          const orgId = await getCurrentOrganisationId();
          const legal_entity_verified = false;
          const legal_entity_verification_status =
            businessModel === 'hire_firm_registered'
              ? 'unverified'
              : 'not_applicable';
          const updates: any = {
            // Business model fields
            business_model: businessModel,
            legal_entity_verified,
            legal_entity_verification_status,
            enable_hire_workflows: nextEnableHireWorkflows,
            enable_transfer_features: nextEnableTransferFeatures
          };

          const { error } = await supabase
            .from('organisations')
            .update(updates)
            .eq('id', orgId);

          if (error) throw error;

          const previousHireToggle = organization?.enable_hire_workflows ?? true;
          const previousTransferToggle = organization?.enable_transfer_features ?? true;
          const toggleDiff: Record<string, { from: boolean; to: boolean }> = {};

          if (previousHireToggle !== nextEnableHireWorkflows) {
            toggleDiff.enable_hire_workflows = {
              from: previousHireToggle,
              to: nextEnableHireWorkflows
            };
          }

          if (previousTransferToggle !== nextEnableTransferFeatures) {
            toggleDiff.enable_transfer_features = {
              from: previousTransferToggle,
              to: nextEnableTransferFeatures
            };
          }

          if (Object.keys(toggleDiff).length > 0) {
            await AuditService.log({
              action: 'fleet_mode_toggle_updated',
              category: 'settings',
              description: 'Fleet mode workflow toggles updated.',
              resource_type: 'organisation',
              resource_id: orgId,
              metadata: toggleDiff
            });
          }
        }

        // Update branding context
        await updateBranding({
          companyName,
          primaryColor: brandingColor,
          logoUrl
        });

        setEnableTransferFeatures(nextEnableTransferFeatures);

        const businessModelChanged =
          initialBusinessModel !== null && initialBusinessModel !== businessModel;
        const hireToggleChanged = (organization?.enable_hire_workflows ?? true) !== nextEnableHireWorkflows;
        const transferToggleChanged =
          (organization?.enable_transfer_features ?? true) !== nextEnableTransferFeatures;
        const shouldReload = businessModelChanged || hireToggleChanged || transferToggleChanged;

        setInitialBusinessModel(businessModel);
        setMessage({
          type: 'success',
          text: shouldReload
            ? 'Organization settings saved successfully. Reloading...'
            : 'Organization settings saved successfully.'
        });

        // Reload only when the business model changes (some feature toggles depend on it).
        if (shouldReload) {
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }

      if (activeTab === 'profile' && !isMockMode) {
        const { error } = await supabase.auth.updateUser({
          data: { email_notifications: notifications }
        });
        if (error) throw error;

        setMessage({ type: 'success', text: 'Settings saved successfully.' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileChange = async (file: File | null) => {
    if (!file) return;
    setLogoPreviewError(false);

    const maxBytes = 300 * 1024;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file (PNG, SVG, JPG).' });
      return;
    }
    if (file.size > maxBytes) {
      setMessage({ type: 'error', text: 'Logo file is too large. Please use an image under 300KB.' });
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo file'));
      reader.readAsDataURL(file);
    });

    setLogoUrl(dataUrl);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTrialWorkspace) {
      setMessage({
        type: 'error',
        text: 'Team invites unlock after upgrading from the 3-PCN trial.'
      });
      setIsAddUserModalOpen(false);
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    if (tier === 'starter' && teamMembers.length >= 1) {
      setMessage({
        type: 'error',
        text: 'Your current plan has reached its team seat limit.'
      });
      setIsAddUserModalOpen(false);
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      if (isMockMode) {
        const newMember: UserProfile = {
          id: `u-${Date.now()}`,
          name: newUser.name,
          email: newUser.email,
          role: newUser.role,
        };
        setTeamMembers([...teamMembers, newMember]);
        setIsAddUserModalOpen(false);
        setNewUser({ name: '', email: '', role: 'viewer' });
        setMessage({ type: 'success', text: `User ${newMember.name} added successfully.` });
        setTimeout(() => setMessage(null), 3000);
        return;
      }

      const payload = {
        email: newUser.email.trim(),
        full_name: newUser.name.trim(),
        role: newUser.role,
      };

      const { data, error } = await supabase.functions.invoke('invite-team-member', {
        body: payload,
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Failed to invite team member');

      setIsAddUserModalOpen(false);
      setNewUser({ name: '', email: '', role: 'viewer' });
      setMessage({ type: 'success', text: `Invite sent to ${payload.email}.` });
      setTimeout(() => setMessage(null), 4000);
      await loadTeam();
    } catch (error: any) {
      console.error('Error adding team member:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMemberRole = async (memberId: string, nextRole: UserRole) => {
    const member = teamMembers.find((m) => m.id === memberId);
    if (!member) return;

    const currentUserId = session?.user?.id ?? null;
    const adminCount = teamMembers.filter((m) => isAdminRole(m.role)).length;
    if (member.role === 'owner') {
      setMessage({ type: 'error', text: 'The organisation owner role cannot be changed here.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    if (isAdminRole(member.role) && !isAdminRole(nextRole) && adminCount <= 1) {
      setMessage({ type: 'error', text: 'Every organisation must have at least one owner or admin.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    if (currentUserId && memberId === currentUserId && nextRole !== 'admin') {
      setMessage({ type: 'error', text: 'You cannot remove your own admin access.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase.from('profiles').update({ role: nextRole }).eq('id', memberId);
      if (error) throw error;
      await loadTeam();
    } catch (error: any) {
      console.error('Error updating member role:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    const member = teamMembers.find((m) => m.id === id);
    if (!member) return;

    const currentUserId = session?.user?.id ?? null;
    const adminCount = teamMembers.filter((m) => isAdminRole(m.role)).length;
    if (member.role === 'owner') {
      setMessage({ type: 'error', text: 'The organisation owner cannot be removed here.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    if (isAdminRole(member.role) && adminCount <= 1) {
      setMessage({ type: 'error', text: 'Every organisation must have at least one owner or admin.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    if (currentUserId && id === currentUserId) {
      setMessage({ type: 'error', text: 'You cannot remove yourself.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    if (!window.confirm(`Remove ${member.name} from your organisation?`)) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('profiles')
        .update({ organisation_id: null, role: 'viewer' })
        .eq('id', id);
      if (error) throw error;
      await loadTeam();
    } catch (error: any) {
      console.error('Error removing team member:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Stripe Connect Handlers
  const handleConnectStripe = async () => {
    if (isMockMode) {
      setMessage({ type: 'error', text: 'Stripe Connect is not available in demo mode.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    try {
      setConnectingStripe(true);
      setMessage(null);

      const orgId = await getCurrentOrganisationId();
      console.log('Organisation ID to send:', orgId);
      console.log('Current organisation state:', organization);

      if (!orgId) {
        throw new Error('No organisation ID found. Please ensure you are logged in and have an organisation.');
      }

      const requestBody = {
        action: 'create_account_link',
        organisationId: orgId,
      };
      console.log('Request body:', requestBody);

      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: requestBody,
      });
      if (error) throw error;

      if (!data.success || !data.url) {
        throw new Error(data.error || 'Failed to create Stripe Connect link');
      }

      // Redirect to Stripe onboarding
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Caught error:', error);
      console.error('Error.message:', error?.message);
      console.error('Error keys:', Object.keys(error));
      console.error('Error.context:', error?.context);
      console.error('Error prototype:', Object.getPrototypeOf(error));

      // Try to get all properties including non-enumerable ones
      const allProps = Object.getOwnPropertyNames(error);
      console.error('All property names:', allProps);
      allProps.forEach(prop => {
        console.error(`  ${prop}:`, error[prop]);
      });

      // Extract error message
      let errorMessage = error?.message || getErrorMessage(error);

      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleRefreshStripeAccount = async () => {
    if (isMockMode || !stripeConnectAccountId) return;

    try {
      setSaving(true);
      setMessage(null);

      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: {
          action: 'refresh_account',
          accountId: stripeConnectAccountId,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to refresh Stripe account');

      // Update local state
      setStripeConnectStatus(data.account.status);
      setStripeChargesEnabled(data.account.chargesEnabled);
      setStripePayoutsEnabled(data.account.payoutsEnabled);

      setMessage({ type: 'success', text: 'Stripe account status refreshed successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error refreshing Stripe account:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleGetDashboardLink = async () => {
    if (isMockMode || !stripeConnectAccountId) return;

    try {
      setSaving(true);
      setMessage(null);

      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: {
          action: 'get_dashboard_link',
          accountId: stripeConnectAccountId,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to get Stripe dashboard link');

      // Open Stripe Express Dashboard in new tab
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('Error getting Stripe dashboard link:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (isMockMode || !stripeConnectAccountId) return;

    if (!window.confirm('Are you sure you want to disconnect your Stripe account? This will prevent you from creating new driver invoices until you reconnect.')) {
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: {
          action: 'disconnect_account',
          organisationId: orgId,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to disconnect Stripe account');

      // Reset local state
      setStripeConnectAccountId(null);
      setStripeConnectStatus('not_connected');
      setStripeChargesEnabled(false);
      setStripePayoutsEnabled(false);

      setMessage({ type: 'success', text: 'Stripe account disconnected successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error disconnecting Stripe:', error);
      setMessage({ type: 'error', text: getErrorMessage(error) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      
      {/* Header & Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Settings</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage your account{canManageTeam ? ', team, and organization' : ''}.</p>
        </div>
        
        {/* Mock Role Switcher for Demo - Development Only */}
        {isMockMode && (import.meta as any).env?.VITE_ENVIRONMENT === 'development' && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-900">
            <span className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase">Demo:</span>
            <button
              onClick={() => setMockRole('owner')}
              className={`text-xs px-2 py-1 rounded ${role === 'owner' ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40'}`}
            >
              As Owner
            </button>
            <button
              onClick={() => setMockRole('viewer')}
              className={`text-xs px-2 py-1 rounded ${role === 'viewer' ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40'}`}
            >
              As Viewer
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
        
        {/* Tab Navigation */}
        <div className="border-b border-slate-200 dark:border-slate-800 p-2">
          <div className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-h-10 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-800'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {!canManageTeam && (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
              <Shield size={14} className="mt-0.5 shrink-0 text-slate-400" />
              <span>Only owners and admins can manage team, organization, billing, and audit settings.</span>
            </div>
          )}
        </div>

        {/* --- PROFILE TAB --- */}
        {activeTab === 'profile' && (
          <form onSubmit={handleSave} className="p-6 space-y-6">
            <div className="max-w-2xl">
              <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Key size={20} className="text-slate-400" /> Account Security</h2>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
                  <input 
                    type="email" 
                    value={email}
                    disabled
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-lg text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-400 mt-1">To change your email, please contact support.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 dark:border-slate-800 pt-6 max-w-2xl">
              <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Bell size={20} className="text-slate-400" /> Notifications</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Email Notifications</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Receive summaries of new tickets and appeals.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={notifications}
                    onChange={(e) => setNotifications(e.target.checked)}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 dark:peer-focus:ring-primary-900 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          </form>
        )}

        {/* --- TEAM TAB (Admin Only) --- */}
        {activeTab === 'team' && canManageTeam && (
          <div className="p-6">
            {isTrialWorkspace ? (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                      Single-user trial
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Team invites unlock after the organisation upgrades from the 3-PCN trial.
                    </p>
                  </div>
                </div>
              </div>
            ) : tier === 'starter' && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                      Starter Plan Limitation
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      Your Starter plan is limited to a single user. Upgrade to Pro Fleet for team collaboration.
                    </p>
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="text-sm font-medium text-amber-900 dark:text-amber-100 underline hover:no-underline"
                    >
                      View Pro Features →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {message && (
              <div className={`mb-4 p-3 rounded-lg border text-sm flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900 text-green-700 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900 text-red-700 dark:text-red-300'
              }`}>
                {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                <span>{message.text}</span>
              </div>
            )}

            {teamLoading && (
              <div className="mb-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                Loading team members…
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-white">Team Members</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Manage access and roles for your fleet.
                  {tier === 'starter' && (
                    <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
                      ({teamMembers.length}/1 member used)
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                disabled={saving || teamLoading || isTrialWorkspace || (tier === 'starter' && teamMembers.length >= 1)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                  saving || teamLoading || isTrialWorkspace || (tier === 'starter' && teamMembers.length >= 1)
                    ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700'
                }`}
              >
                <Plus size={16} /> Add Member
              </button>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Name</th>
                    <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Email</th>
                    <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Role</th>
                    <th className="px-6 py-3 font-medium text-slate-500 dark:text-slate-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {teamMembers.length === 0 && !teamLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                        No team members found.
                      </td>
                    </tr>
                  ) : (
                    teamMembers.map(member => (
                      <tr key={member.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">{member.name}</td>
                        <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{member.email}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <select
                              value={member.role}
                              onChange={(e) => void handleUpdateMemberRole(member.id, e.target.value as UserRole)}
                              disabled={saving || teamLoading || member.id === session?.user?.id || member.role === 'owner'}
                              className={`text-xs font-medium border rounded-full px-2.5 py-1 bg-white dark:bg-slate-900 outline-none ${
                                isAdminRole(member.role)
                                  ? 'border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300'
                                  : 'border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
                              } ${saving || teamLoading || member.id === session?.user?.id || member.role === 'owner' ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-label={`Role for ${member.email}`}
                            >
                              {member.role === 'owner' && <option value="owner">Owner</option>}
                              <option value="admin">Admin</option>
                              <option value="viewer">Viewer</option>
                            </select>
                            {member.id === session?.user?.id && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50">
                                You
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => void handleDeleteUser(member.id)}
                            disabled={saving || teamLoading || member.id === session?.user?.id || member.role === 'owner'}
                            className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={member.id === session?.user?.id ? 'You cannot remove yourself' : 'Remove User'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- ORGANIZATION TAB (Admin Only) --- */}
        {activeTab === 'organization' && canManageTeam && (
          <form onSubmit={handleSave} className="p-6 space-y-6">
            {/* SECTION 1: Operating Profile Selection */}
            <div className="pb-6 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Operating Profile
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Choose the workflow FleetMode should optimize for. Transfer pack readiness is still checked per PCN using hire evidence.
              </p>

              <div className="grid gap-4 md:grid-cols-3">
                {/* PCO Hire Operator Card */}
                <BusinessModelCard
                  icon={ShieldCheck}
                  title="PCO Hire Operator"
                  description="Match PCNs to hirers, check hire evidence, prepare transfer packs, and track recovery."
                  selected={businessModel === 'hire_firm_registered'}
                  onClick={() => selectBusinessModel('hire_firm_registered')}
                  color="primary"
                />

                {/* Existing setup-incomplete PCO profile */}
                <BusinessModelCard
                  icon={AlertTriangle}
                  title="PCO Setup Incomplete"
                  description="Use the PCO hire workflow while company details or agreement evidence are still being completed."
                  selected={businessModel === 'hire_firm_startup'}
                  onClick={() => selectBusinessModel('hire_firm_startup')}
                  color="amber"
                />

                {/* Owned / Managed Fleet Card */}
                <BusinessModelCard
                  icon={Car}
                  title="Owned / Managed Fleet"
                  description="Company vehicles used by staff or contractors. Pay, appeal, or assign tickets internally."
                  selected={businessModel === 'fleet_operator'}
                  onClick={() => selectBusinessModel('fleet_operator')}
                  color="blue"
                />
              </div>

              {/* Setup-incomplete PCO guidance */}
              {businessModel === 'hire_firm_startup' && (
                <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900 dark:text-amber-100">
                      <strong>Setup details incomplete:</strong> Keep using the PCO workflow, but verify each
                      hire agreement and PCN evidence before preparing a transfer pack.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {businessModel === 'fleet_operator' && (
              <div className="pb-6 border-b border-slate-100 dark:border-slate-800">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  Fleet Mode Module Access
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Owned fleet mode hides hire workflows by default. Enable only if this workspace also needs hire records or driver invoices.
                </p>

                <label className="flex items-start justify-between gap-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      Enable hire workflows
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Shows Hires and Driver Invoices modules. Turning this off also disables transfer-pack features.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={enableHireWorkflows}
                    onChange={(e) => {
                      const nextChecked = e.target.checked;
                      setEnableHireWorkflows(nextChecked);
                      setEnableTransferFeatures(nextChecked);
                    }}
                    className="mt-1 h-4 w-4 text-primary-600 rounded border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Transfer features status: <span className="font-semibold text-slate-700 dark:text-slate-200">{enableTransferFeatures ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            )}

            {/* SECTION 2: Company Setup Details (conditional) */}
            {businessModel === 'hire_firm_registered' && (
              <div className="pb-6 border-b border-slate-100 dark:border-slate-800">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  Company Setup Details
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Organisation setup does not decide transfer readiness. FleetMode checks each PCN against the hire record and agreement evidence.
                </p>

                <div className="flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900/40">
                  <CheckCircle2 size={18} className="mt-0.5 text-primary-600 dark:text-primary-400" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      Use the Hire section to verify agreement evidence
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      A transfer pack is prepared only after the workflow checks hire dates, uploaded agreement,
                      liability wording, hirer details, and PCN type/stage.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 3: Payment Processing (Stripe Connect) */}
            <div className="pb-6 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <CreditCard size={20} className="text-slate-400" />
                Payment Processing
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Connect your Stripe account to receive driver invoice payments directly to your bank account
              </p>

              {stripeConnectStatus === 'not_connected' ? (
                /* Not Connected State */
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                        Connect your Stripe account
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                        To create driver invoices and receive payments, you need to connect a Stripe account.
                        Driver payments will go directly to your bank account, and we charge a 2% platform fee.
                      </p>
                      <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Payments go directly to your bank account</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Professional Stripe-hosted invoice pages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Automatic payment tracking and notifications</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <span>2% platform fee applied to driver invoice payments</span>
                        </li>
                      </ul>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleConnectStripe}
                        disabled={connectingStripe || isMockMode}
                        className="px-6 py-3 bg-[#635BFF] hover:bg-[#5147ED] text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {connectingStripe ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <LinkIcon size={16} />
                            Connect Stripe
                          </>
                        )}
                      </button>
                      {isMockMode && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                          Not available in demo mode
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : stripeConnectStatus === 'pending' ? (
                /* Pending State */
                <div className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                        Stripe account onboarding in progress
                      </h4>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
                        You've started connecting your Stripe account, but the onboarding process is not yet complete.
                        Please finish the setup to start receiving driver invoice payments.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleConnectStripe}
                          disabled={connectingStripe}
                          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {connectingStripe ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <ExternalLink size={16} />
                              Continue Setup
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleRefreshStripeAccount}
                          disabled={saving}
                          className="px-4 py-2 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-800 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Refresh Status
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : stripeConnectStatus === 'active' ? (
                /* Active State */
                <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                      <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                        Stripe account connected
                      </h4>
                      <p className="text-sm text-green-800 dark:text-green-200 mb-4">
                        Your Stripe account is fully connected and ready to receive driver invoice payments.
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${stripeChargesEnabled ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                          <span className="text-green-900 dark:text-green-100">
                            {stripeChargesEnabled ? 'Charges enabled' : 'Charges disabled'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${stripePayoutsEnabled ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                          <span className="text-green-900 dark:text-green-100">
                            {stripePayoutsEnabled ? 'Payouts enabled' : 'Payouts disabled'}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleGetDashboardLink}
                          disabled={saving}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ExternalLink size={16} />
                          Open Stripe Dashboard
                        </button>
                        <button
                          onClick={handleRefreshStripeAccount}
                          disabled={saving}
                          className="px-4 py-2 border border-green-300 dark:border-green-700 text-green-900 dark:text-green-100 hover:bg-green-100 dark:hover:bg-green-800 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Refresh Status
                        </button>
                        <button
                          onClick={handleDisconnectStripe}
                          disabled={saving}
                          className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Restricted/Disabled State */
                <div className="border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 dark:bg-red-800 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                        Stripe account {stripeConnectStatus}
                      </h4>
                      <p className="text-sm text-red-800 dark:text-red-200 mb-4">
                        Your Stripe account cannot currently accept payments. Please contact Stripe support or check your Stripe dashboard for more information.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleGetDashboardLink}
                          disabled={saving}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ExternalLink size={16} />
                          Open Stripe Dashboard
                        </button>
                        <button
                          onClick={handleRefreshStripeAccount}
                          disabled={saving}
                          className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-900 dark:text-red-100 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Refresh Status
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 4: Company Branding */}
            <div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-4">
                Company Branding
              </h3>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="Your Company Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Brand Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={brandingColor}
                      onChange={(e) => setBrandingColor(e.target.value)}
                      className="h-10 w-20 rounded border border-slate-300 dark:border-slate-700 cursor-pointer p-1 bg-white dark:bg-slate-800"
                    />
                    <input
                      type="text"
                      value={brandingColor}
                      onChange={(e) => setBrandingColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="#2563EB"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Tip: Very light colors can make button text hard to read. We automatically adjust overly bright choices to keep the UI legible.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Logo URL</label>
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                      <ImageIcon className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <input
                        type="url"
                        value={logoUrl}
                        onChange={(e) => {
                          setLogoPreviewError(false);
                          setLogoUrl(e.target.value);
                        }}
                        className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                      <label className="inline-flex items-center justify-center px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer whitespace-nowrap">
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => void handleLogoFileChange(e.target.files?.[0] ?? null)}
                        />
                      </label>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                        {logoUrl && !logoPreviewError ? (
                          <img
                            src={logoUrl}
                            alt="Logo preview"
                            className="w-full h-full object-contain"
                            onError={() => setLogoPreviewError(true)}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Logo preview (updates in the sidebar after saving).
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Provide a direct URL to your company logo (PNG or SVG recommended), or upload an image.</p>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* --- DRIVER BILLING TAB (Admin Only) --- */}
        {activeTab === 'driver-billing' && canManageTeam && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                Driver Billing Settings
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Configure how drivers are charged for parking tickets and admin fees.
              </p>
            </div>
            <DriverBillingSettings />
          </div>
        )}

        {/* --- AUDIT LOGS TAB (Admin Only) --- */}
        {activeTab === 'audit-logs' && canManageTeam && (
          <div className="p-6">
            <AuditLogs />
          </div>
        )}

        {/* Footer Actions (Only show if relevant to tab) */}
        {(activeTab === 'profile' || activeTab === 'organization') && (
          <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div>
              {message && (
                <div className={`flex items-center gap-2 text-sm font-medium animate-in fade-in slide-in-from-left-2 ${
                  message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  {message.text}
                </div>
              )}
            </div>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        title="Add Team Member"
        maxWidth="max-w-md"
      >
        <form onSubmit={handleAddUser} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
            <input 
              type="text" 
              required
              value={newUser.name}
              onChange={(e) => setNewUser({...newUser, name: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              placeholder="e.g. Sarah Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
            <input 
              type="email" 
              required
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              placeholder="sarah@company.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Role</label>
            <div className="grid grid-cols-2 gap-3">
              <div 
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  newUser.role === 'viewer'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-500' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
                onClick={() => setNewUser({...newUser, role: 'viewer'})}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users size={16} className={newUser.role === 'viewer' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'} />
                  <span className="font-medium text-sm text-slate-900 dark:text-white">Viewer</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Can view fleet workflows without changing team or billing settings.</p>
              </div>
              
              <div 
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  newUser.role === 'admin' 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 ring-1 ring-purple-500' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
                onClick={() => setNewUser({...newUser, role: 'admin'})}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={16} className={newUser.role === 'admin' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400'} />
                  <span className="font-medium text-sm text-slate-900 dark:text-white">Admin</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Full access to settings, billing, and users.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsAddUserModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send Invite'
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Settings;
